import { useMemo, useState } from 'react'
import { Box, Button, Chip, List, ListItem, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import HistoryIcon from '@mui/icons-material/History'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import { com } from '@eidu/web'
import { VersionRecord } from '../../../../../io/api/entity/data/listVersions'
import EntityLink from '../../../../../components/data/EntityLink'
import VersionDiffDialog from './VersionDiffDialog'
import useVersions from './useVersions'
import EntityId = com.eidu.sharedlib.entity.EntityId

type VersionHistoryProps = {
  entityId: EntityId
  initialCompareVersionId?: string
}

type DiffPair = { left: string; right: string }

const absoluteTimestamp = (ms: number): string => new Date(ms).toLocaleString()

const relativeTimestamp = (ms: number, now: number = Date.now()): string => {
  const diffSec = Math.round((ms - now) / 1000)
  const abs = Math.abs(diffSec)
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  if (abs < 60) return rtf.format(diffSec, 'second')
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour')
  if (abs < 2592000) return rtf.format(Math.round(diffSec / 86400), 'day')
  if (abs < 31536000) return rtf.format(Math.round(diffSec / 2592000), 'month')
  return rtf.format(Math.round(diffSec / 31536000), 'year')
}

const shortVersionId = (versionId: string): string => versionId.split('-')[0] ?? versionId.slice(0, 8)

type VersionRowProps = {
  record: VersionRecord
  isLatest: boolean
  onCompare: () => void
  compareDisabledReason?: string
}

const VersionRow = ({ record, isLatest, onCompare, compareDisabledReason }: VersionRowProps) => (
  <ListItem disableGutters sx={{ display: 'block', py: 1.5 }}>
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5, justifyContent: 'space-between' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Tooltip title={record.versionId}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, letterSpacing: 0.2 }}>
            {shortVersionId(record.versionId)}
          </Typography>
        </Tooltip>
        {isLatest && <Chip label="Latest" size="small" color="primary" variant="outlined" />}
      </Stack>
      <Tooltip title={compareDisabledReason ?? 'Compare against latest'}>
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CompareArrowsIcon />}
            onClick={onCompare}
            disabled={!!compareDisabledReason}
            sx={{ textTransform: 'none' }}
          >
            Compare
          </Button>
        </span>
      </Tooltip>
    </Stack>
    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 0.5, color: 'text.secondary' }}>
      <Tooltip title={absoluteTimestamp(record.createdAt)}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <ScheduleOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption">{relativeTimestamp(record.createdAt)}</Typography>
        </Stack>
      </Tooltip>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', minWidth: 0 }}>
        <AccountCircleOutlinedIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption" noWrap>
          <EntityLink entityId={record.createdBy} variant="emphasized" showTooltip={false} />
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <HubOutlinedIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption">
          {record.dependencyCount} {record.dependencyCount === 1 ? 'dependency' : 'dependencies'}
        </Typography>
      </Stack>
    </Stack>
  </ListItem>
)

const VersionHistory = ({ entityId, initialCompareVersionId }: VersionHistoryProps) => {
  const { records, error } = useVersions(entityId)
  const [overridePair, setOverridePair] = useState<DiffPair | null>(null)
  const [dismissed, setDismissed] = useState(false)

  const autoPair = useMemo<DiffPair | null>(() => {
    if (!initialCompareVersionId || !records) return null
    const idx = records.findIndex((r) => r.versionId === initialCompareVersionId)
    if (idx === -1) return null
    const right = records[idx].versionId
    const left = (records[idx + 1] ?? records[idx]).versionId
    return { left, right }
  }, [initialCompareVersionId, records])

  const diffPair = overridePair ?? (dismissed ? null : autoPair)

  const closeDiff = () => {
    setOverridePair(null)
    setDismissed(true)
  }

  if (error) return <Typography color="error">Failed to load version history: {error.message}</Typography>
  if (records === undefined)
    return (
      <List disablePadding sx={{ '& > li + li': { borderTop: 1, borderColor: 'divider' } }} aria-busy>
        {Array.from({ length: 3 }).map((_, i) => (
          <ListItem key={i} disableGutters sx={{ display: 'block', py: 1.5 }}>
            <Skeleton variant="text" width={72} height={20} sx={{ mb: 0.75 }} />
            <Stack direction="row" spacing={2}>
              <Skeleton variant="text" width={90} height={16} />
              <Skeleton variant="text" width={120} height={16} />
              <Skeleton variant="text" width={100} height={16} />
            </Stack>
          </ListItem>
        ))}
      </List>
    )
  if (records.length === 0)
    return (
      <Stack
        sx={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 3,
          gap: 1.5,
          color: 'text.secondary',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <HistoryIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
        </Box>
        <Typography variant="subtitle1" color="text.primary" sx={{ mt: 1 }}>
          No versions yet
        </Typography>
        <Typography variant="body2" sx={{ maxWidth: 280 }}>
          Publish this asset to create an immutable snapshot. Each publish will appear here.
        </Typography>
      </Stack>
    )

  const latest = records[0]
  const canCompareBase = records.length >= 2

  return (
    <>
      <List disablePadding sx={{ '& > li + li': { borderTop: 1, borderColor: 'divider' } }}>
        {records.map((record, index) => {
          const isLatest = index === 0
          const selfCompare = isLatest
          const disabledReason = !canCompareBase
            ? 'Need at least two versions to compare'
            : selfCompare
              ? 'This is the latest version'
              : undefined
          return (
            <VersionRow
              key={record.versionId}
              record={record}
              isLatest={isLatest}
              onCompare={() => setOverridePair({ left: latest.versionId, right: record.versionId })}
              compareDisabledReason={disabledReason}
            />
          )
        })}
      </List>
      {diffPair && (
        <VersionDiffDialog
          onClose={closeDiff}
          records={records}
          initialLeftVersionId={diffPair.left}
          initialRightVersionId={diffPair.right}
        />
      )}
    </>
  )
}

export default VersionHistory
